import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function ConfirmEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { confirmEmail } = useAuth();
  const email = location.state?.email || '';
  const prefillCode = location.state?.code || '';

  const [digits, setDigits] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (prefillCode && prefillCode.length === 4) {
      setDigits(prefillCode.split(''));
    }
  }, [prefillCode]);

  const handleDigit = (i, val) => {
    const sanitized = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = sanitized;
    setDigits(next);
    setError('');
    if (sanitized && i < 3) inputRefs[i + 1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputRefs[i - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setDigits(pasted.split(''));
      inputRefs[3].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 4) { setError('Enter the 4-digit code.'); return; }
    if (!email) { setError('Email not found. Go back and try again.'); return; }
    setLoading(true);
    setError('');
    try {
      await confirmEmail(email, code);
      navigate('/app/feed');
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
      setDigits(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const codeComplete = digits.every(d => d !== '');

  return (
    <div className="auth-root">
      <div className="auth-blobs">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
      </div>
      <div className="auth-card-wrap">
        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <img src="/rivalogo.png" alt="RivalCam" className="auth-logo" />
          </Link>
          <h1 className="auth-title">Confirm your email</h1>
          <p className="auth-subtitle">Enter the 4-digit code we sent to your inbox.</p>
        </div>
        <div className="auth-card">
          <div className="auth-card-stripe" />
          <div className="confirm-body">
            <div className="confirm-icon-wrap">
              <Mail size={40} color="#38BDF8" />
            </div>
            {email && (
              <p style={{ color: 'rgba(199,210,254,0.7)', fontSize: '14px', textAlign: 'center', lineHeight: '1.6', marginBottom: '24px' }}>
                Code sent to <strong style={{ color: '#BAE6FD' }}>{email}</strong>
              </p>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '8px' }}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={inputRefs[i]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => handleDigit(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    style={{
                      width: '56px',
                      height: '64px',
                      textAlign: 'center',
                      fontSize: '28px',
                      fontWeight: '700',
                      background: 'rgba(14,165,233,0.08)',
                      border: `2px solid ${error ? '#F87171' : d ? '#0EA5E9' : 'rgba(56,189,248,0.25)'}`,
                      borderRadius: '12px',
                      color: '#E0E7FF',
                      outline: 'none',
                      caretColor: '#38BDF8',
                      transition: 'border-color 0.15s',
                    }}
                  />
                ))}
              </div>

              {error && <div className="auth-server-error" style={{ marginTop: '8px' }}>{error}</div>}

              <button
                type="submit"
                className="auth-btn"
                disabled={!codeComplete || loading}
                style={{ marginTop: '24px' }}
              >
                {loading ? <span className="auth-spinner" /> : <span>Verify &amp; Sign In</span>}
              </button>
            </form>

            <Link to="/login" className="auth-back-btn" style={{ marginTop: '16px', display: 'flex', textDecoration: 'none' }}>
              <ArrowLeft size={16} />
              <span>Back to Sign In</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
